import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { FiDownload, FiFileText, FiFilter, FiInfo, FiRefreshCw, FiAlertTriangle, FiPlus } from 'react-icons/fi';
import styles from './ReportsPage.module.css';
import { ReportsService, ReportItem, ReportType } from '../services/reports.service';

const typeKey: Record<ReportType, string> = {
  statement: 'reports.type_statement',
  invoice: 'reports.type_invoice',
  performance: 'reports.type_performance',
  generic: 'reports.type_generic'
};

function safeT(t: (k: string, opt?: any) => string, key: string, fallback: string) {
  const v = t(key);
  return v === key ? fallback : v;
}

function downloadBlob(blob: Blob, filename: string) {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename || 'reporte.pdf';
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}

export default function ReportsPage() {
  const { t } = useTranslation();

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [generating, setGenerating] = useState(false);
  const [downloadingId, setDownloadingId] = useState<string | number | null>(null);

  const [items, setItems] = useState<ReportItem[]>([]);
  const [type, setType] = useState<ReportType | 'all'>('all');

  const load = async () => {
    setLoading(true);
    setError(null);

    try {
      const res = await ReportsService.getMyReports();
      setItems(Array.isArray(res) ? res : []);
    } catch {
      setItems([]);
      setError(safeT(t, 'reports.load_error', 'No se pudieron cargar los reportes.'));
    } finally {
      setLoading(false);
    }
  };

  const generateReport = async (type: 'statement' | 'performance') => {
    setGenerating(true);
    try {
      let newReport: ReportItem;
      if (type === 'statement') {
        newReport = await ReportsService.generateStatement();
      } else {
        newReport = await ReportsService.generatePerformanceReport();
      }
      
      // Actualizar la lista con el nuevo reporte
      setItems(prev => [newReport, ...prev]);
    } catch (error: any) {
      setError(error?.payload?.message || error?.message || safeT(t, 'reports.generate_error', 'No se pudo generar el reporte.'));
    } finally {
      setGenerating(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const filtered = useMemo(() => {
    if (type === 'all') return items;
    return items.filter(x => x.type === type);
  }, [items, type]);

  const downloadReport = async (item: ReportItem, format: 'pdf' | 'csv' = 'pdf') => {
    setError(null);
    setDownloadingId(`${item.id}:${format}`);
    try {
      const res = await ReportsService.downloadReportFile(item.id, { format, type: item.type });
      downloadBlob(res.blob, res.filename);
    } catch (error: any) {
      setError(error?.payload?.message || error?.message || safeT(t, 'reports.download_error', 'No se pudo descargar el reporte.'));
    } finally {
      setDownloadingId(null);
    }
  };

  return (
    <div className={styles.page}>
      <header className={styles.header}>
        <div className={styles.headerText}>
          <h1 className={styles.title}>{safeT(t, 'reports.page_title', 'Mis Reportes')}</h1>
          <p className={styles.subtitle}>
            {safeT(t, 'reports.page_subtitle', 'Descarga tus comprobantes fiscales y estados de cuenta.')}
          </p>
        </div>

        <div className={styles.toolbar}>
          <div className={styles.selectWrap}>
            <FiFilter className={styles.icon} />
            <select
              value={type}
              onChange={e => setType(e.target.value as any)}
              className={styles.select}
              disabled={loading}
              aria-label={safeT(t, 'reports.filter', 'Filtrar')}
            >
              <option value="all">{safeT(t, 'reports.filter', 'Filtrar')}</option>
              <option value="statement">{safeT(t, 'reports.type_statement', 'Estado de Cuenta')}</option>
              <option value="invoice">{safeT(t, 'reports.type_invoice', 'Factura')}</option>
              <option value="performance">{safeT(t, 'reports.type_performance', 'Rendimiento')}</option>
              <option value="generic">{safeT(t, 'reports.type_generic', 'Documento')}</option>
            </select>
          </div>

          <div className={styles.actionButtons}>
            <button
              type="button"
              className={styles.generateBtn}
              onClick={() => generateReport('statement')}
              disabled={loading || generating}
            >
              <FiPlus />
              {safeT(t, 'reports.generate_statement', 'Estado de Cuenta')}
            </button>

            <button
              type="button"
              className={styles.generateBtn}
              onClick={() => generateReport('performance')}
              disabled={loading || generating}
            >
              <FiPlus />
              {safeT(t, 'reports.generate_performance', 'Reporte de Rendimiento')}
            </button>

            <button type="button" className={styles.refreshBtn} onClick={load} disabled={loading}>
              <FiRefreshCw />
              {safeT(t, 'common.refresh', 'Actualizar')}
            </button>
          </div>
        </div>
      </header>

      {/* Estados consistentes: loading -> error -> empty -> list */}
      <section className={styles.card}>
        {loading ? (
          <div className={styles.state}>
            <div className={styles.stateTitle}>{safeT(t, 'common.loading', 'Cargando...')}</div>
          </div>
        ) : error ? (
          <div className={styles.state}>
            <div className={styles.stateIconError}>
              <FiAlertTriangle />
            </div>
            <div className={styles.stateTitle}>{safeT(t, 'reports.error_title', 'No se pudo cargar la información')}</div>
            <div className={styles.stateText}>{error}</div>

            <button type="button" className={styles.primaryBtn} onClick={load}>
              <FiRefreshCw />
              {safeT(t, 'common.refresh', 'Reintentar')}
            </button>
          </div>
        ) : filtered.length === 0 ? (
          <div className={styles.state}>
            <div className={styles.stateIcon}>
              <FiInfo />
            </div>
            <div className={styles.stateTitle}>
              {safeT(t, 'reports.no_reports_available', 'No hay reportes disponibles para descargar.')}
            </div>
            <div className={styles.stateText}>
              {safeT(
                t,
                'reports.no_reports_hint',
                'Cuando generes comprobantes o estados de cuenta, aparecerán aquí para descarga.'
              )}
            </div>
          </div>
        ) : (
          <div className={styles.list}>
            {filtered.map(r => (
              <article key={String(r.id)} className={styles.row}>
                <div className={styles.rowLeft}>
                  <div className={styles.rowIcon}>
                    <FiFileText />
                  </div>

                  <div className={styles.rowText}>
                    <div className={styles.rowTitle}>
                      {r.title?.trim() || safeT(t, typeKey[r.type], 'Documento')}
                    </div>

                    <div className={styles.rowMeta}>
                      <span className={styles.chip}>{safeT(t, typeKey[r.type], r.type)}</span>
                      <span className={styles.dot}>•</span>
                      <span>{r.date}</span>
                    </div>
                  </div>
                </div>

                <div className={styles.rowActions}>
                  <button
                    type="button"
                    className={styles.downloadBtn}
                    onClick={() => downloadReport(r, 'pdf')}
                    disabled={downloadingId === `${r.id}:pdf` || downloadingId === `${r.id}:csv`}
                  >
                    <FiDownload />
                    {downloadingId === `${r.id}:pdf`
                      ? safeT(t, 'common.loading', 'Cargando...')
                      : `${safeT(t, 'reports.download', 'Descargar')} PDF`}
                  </button>

                  {r.type === 'statement' || r.type === 'performance' ? (
                    <button
                      type="button"
                      className={styles.downloadBtnSecondary}
                      onClick={() => downloadReport(r, 'csv')}
                      disabled={downloadingId === `${r.id}:pdf` || downloadingId === `${r.id}:csv`}
                    >
                      <FiDownload />
                      {downloadingId === `${r.id}:csv` ? '...' : 'CSV'}
                    </button>
                  ) : null}
                </div>
              </article>
            ))}
          </div>
        )}
      </section>
    </div>
  );
}
